import { useEffect, useRef } from "react";
import { useSupplier } from "../../../../hooks/query";
import useSelectedSupplier from "./useSelectedSupplier";

interface UseSupplierPricingProps {
    selectedValues: any;
    setSelectedValues: (updater: (prev: any) => any) => void;
}

/**
 * Hook to handle supplier price updates when supplier selection changes
 */
export function useSupplierPricing({ selectedValues, setSelectedValues }: UseSupplierPricingProps) {
    const { selectedSupplier } = useSelectedSupplier();
    const { useListInventoryMutation } = useSupplier();
    const listInventoryMutation = useListInventoryMutation();

    const isUpdatingPrices = useRef<boolean>(false);
    const lastSupplierId = useRef<string | null>(null);
    const lastSupplierName = useRef<string | null>(null);

    const updatePricesFromSupplier = async () => {
        // Prevent infinite loops
        if (isUpdatingPrices.current) {
            return;
        }

        // Skip if supplier hasn't changed
        const currentSupplierId = selectedSupplier?.id || null;
        const currentSupplierName = selectedSupplier?.name || null;
        if (
            currentSupplierId === lastSupplierId.current &&
            currentSupplierName === lastSupplierName.current &&
            Object.keys(selectedValues).length > 0
        ) {
            return;
        }

        isUpdatingPrices.current = true;
        lastSupplierId.current = currentSupplierId;
        lastSupplierName.current = currentSupplierName;

        // If no supplier selected, reset unit prices
        if (!selectedSupplier?.name) {
            if (Object.keys(selectedValues).length > 0) {
                setSelectedValues((prev: any) => {
                    const updated: any = {};
                    let hasChanges = false;
                    Object.entries(prev).forEach(([category, value]: [string, any]) => {
                        if (value.unitPrice !== undefined || value.supplierId || value.supplierName) {
                            hasChanges = true;
                            updated[category] = {
                                ...value,
                                unitPrice: undefined,
                                supplierId: undefined,
                                supplierName: undefined,
                            };
                        } else {
                            updated[category] = value;
                        }
                    });
                    return hasChanges ? updated : prev;
                });
            }
            isUpdatingPrices.current = false;
            return;
        }

        try {
            // Fetch supplier inventory directly by SupplierId for accurate pricing
            if (!selectedSupplier?.id) {
                isUpdatingPrices.current = false;
                return;
            }

            // Fetch supplier inventory using mutation hook
            const inventory = (await listInventoryMutation.mutateAsync({ SupplierId: selectedSupplier.id })) || [];

            if (inventory.length > 0 && Object.keys(selectedValues).length > 0) {
                setSelectedValues((prev: any) => {
                    const updated: any = {};
                    let hasChanges = false;

                    Object.entries(prev).forEach(([category, selection]: [string, any]) => {
                        const manufacturerProductId = selection?.manufacturerProductId;
                        const productName = selection?.value;

                        if (manufacturerProductId || productName) {
                            // Match by ManufacturerProductId first (most accurate)
                            let matchedItem = manufacturerProductId
                                ? inventory.find((inv: any) => inv.ManufacturerProductId === manufacturerProductId)
                                : null;

                            // Fallback to ProductName if ManufacturerProductId match failed
                            if (!matchedItem && productName) {
                                matchedItem = inventory.find((inv: any) => inv.ProductName === productName);
                            }

                            const unitSize = Number(matchedItem?.SupplierUnitSize || 1);
                            const newUnitPrice = matchedItem?.UnitPrice ? Number(matchedItem.UnitPrice) / unitSize : undefined;

                            // Only update if price or supplier info changed
                            if (
                                selection.unitPrice !== newUnitPrice ||
                                selection.supplierId !== selectedSupplier.id ||
                                selection.supplierName !== selectedSupplier.name
                            ) {
                                hasChanges = true;
                                updated[category] = {
                                    ...selection,
                                    unitPrice: newUnitPrice,
                                    supplierId: selectedSupplier.id,
                                    supplierName: selectedSupplier.name,
                                };
                            } else {
                                updated[category] = selection;
                            }
                        } else {
                            updated[category] = selection;
                        }
                    });

                    return hasChanges ? updated : prev;
                });
            }
        } catch (error) {
            console.error("Error fetching supplier prices:", error);
        } finally {
            isUpdatingPrices.current = false;
        }
    };

    /**
     * Updates prices for items that have supplierId but no unitPrice
     * This handles multi-supplier mode where each product has a different supplier
     */
    const updatePricesForItemsWithSuppliers = async () => {
        if (isUpdatingPrices.current) {
            return;
        }

        // Find items that have supplierId but no unitPrice
        const itemsNeedingPrices: Array<{
            category: string;
            supplierId: string;
            supplierName?: string;
            manufacturerProductId?: string;
            productName?: string;
        }> = [];

        Object.entries(selectedValues).forEach(([category, selection]: [string, any]) => {
            if (selection?.supplierId && !selection?.unitPrice) {
                itemsNeedingPrices.push({
                    category,
                    supplierId: selection.supplierId,
                    supplierName: selection.supplierName,
                    manufacturerProductId: selection.manufacturerProductId,
                    productName: selection.value,
                });
            }
        });

        if (itemsNeedingPrices.length === 0) {
            return;
        }

        isUpdatingPrices.current = true;

        try {
            // Group items by supplier to minimize API calls
            const supplierGroups: Record<string, typeof itemsNeedingPrices> = {};
            itemsNeedingPrices.forEach((item) => {
                if (!supplierGroups[item.supplierId]) {
                    supplierGroups[item.supplierId] = [];
                }
                supplierGroups[item.supplierId].push(item);
            });

            // Fetch inventory for each supplier and update prices
            for (const [supplierId, items] of Object.entries(supplierGroups)) {
                try {
                    const inventory = (await listInventoryMutation.mutateAsync({ SupplierId: supplierId })) || [];

                    if (inventory.length > 0) {
                        setSelectedValues((prev: any) => {
                            const updated: any = {};
                            let hasChanges = false;

                            items.forEach((item) => {
                                const selection = prev[item.category];
                                if (!selection) return;

                                // Match by ManufacturerProductId first (most accurate)
                                let matchedItem = item.manufacturerProductId
                                    ? inventory.find(
                                          (inv: any) => inv.ManufacturerProductId === item.manufacturerProductId
                                      )
                                    : null;

                                // Fallback to ProductName if ManufacturerProductId match failed
                                if (!matchedItem && item.productName) {
                                    matchedItem = inventory.find((inv: any) => inv.ProductName === item.productName);
                                }

                                const unitSize = Number(matchedItem?.SupplierUnitSize || 1);
                                const newUnitPrice = matchedItem?.UnitPrice ? Number(matchedItem.UnitPrice) / unitSize : undefined;

                                if (selection.unitPrice !== newUnitPrice) {
                                    hasChanges = true;
                                    updated[item.category] = {
                                        ...selection,
                                        unitPrice: newUnitPrice,
                                    };
                                } else {
                                    updated[item.category] = selection;
                                }
                            });

                            // Copy unchanged items
                            Object.keys(prev).forEach((key) => {
                                if (!updated[key]) {
                                    updated[key] = prev[key];
                                }
                            });

                            return hasChanges ? updated : prev;
                        });
                    }
                } catch (error) {
                    console.error(`Error fetching prices for supplier ${supplierId}:`, error);
                }
            }
        } catch (error) {
            console.error("Error updating prices for items with suppliers:", error);
        } finally {
            isUpdatingPrices.current = false;
        }
    };

    const selectedValuesKeysCount = Object.keys(selectedValues).length;
    const hasSelectedValues = selectedValuesKeysCount > 0;

    useEffect(() => {
        // Trigger when supplier changes OR when selectedValues are first loaded (on page load)
        if (selectedSupplier?.id && hasSelectedValues) {
            updatePricesFromSupplier();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSupplier?.id, selectedSupplier?.name, selectedValuesKeysCount]);

    useEffect(() => {
        // Update prices for items that have supplierId but no unitPrice (multi-supplier mode)
        // Only run if no context supplier is set (to avoid conflicts)
        if (!selectedSupplier?.id && hasSelectedValues) {
            updatePricesForItemsWithSuppliers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValuesKeysCount]);

    // Also update prices when selectedValues change and contain supplier info
    // This handles the case when cart items are reloaded with supplier info
    useEffect(() => {
        if (hasSelectedValues) {
            // Check if any items have supplierId but no unitPrice
            const hasItemsNeedingPrices = Object.values(selectedValues).some(
                (selection: any) => selection?.supplierId && !selection?.unitPrice
            );

            if (hasItemsNeedingPrices && !selectedSupplier?.id) {
                // Small delay to avoid conflicts with other updates
                const timer = setTimeout(() => {
                    updatePricesForItemsWithSuppliers();
                }, 100);
                return () => clearTimeout(timer);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValues]);

    // Listen for cart items update event to refresh prices after supplier confirmation
    useEffect(() => {
        const handleCartItemsUpdate = () => {
            // Small delay to ensure cart items are reloaded
            setTimeout(() => {
                if (selectedSupplier?.id && hasSelectedValues) {
                    updatePricesFromSupplier();
                } else if (hasSelectedValues) {
                    updatePricesForItemsWithSuppliers();
                }
            }, 200);
        };

        window.addEventListener("cartItemsUpdated", handleCartItemsUpdate);
        return () => {
            window.removeEventListener("cartItemsUpdated", handleCartItemsUpdate);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSupplier?.id, hasSelectedValues]);
}
